import { Input } from "@/components/ui/input";
import {
	InputSideLabel,
	InputSideLabelPopover,
} from "@/components/ui/InputSideLabel";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { TriangleAlertIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { t } from "i18next";
import { ParameterInputProps } from "./types";
import { getInputClass } from "./utils";

/** Only Utvidet YS (Ulykke) and Utvidet YS (Sykdom) get the additional YS help text. */
const isExtendedYs = (type: string) =>
	type === "extended-wc-accident" || type === "extended-wc-illness";

export const BaseCompensationInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"base-compensation">) => {
	const content = isExtendedYs(insurance.type)
		? `${t("admin.insurances.parameters.content.base-compensation")} ${t("admin.insurances.parameters.content.additional-text-ys")}`
		: t("admin.insurances.parameters.content.base-compensation");

	return (
		<div className="relative inline-flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					const value = e.target.value;
					parameter.value = value;
					parameter.error = null;

					if (value.length < 1) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-required"
						);
					}
					if (value.length > 5) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-max"
						);
					}
					if (Number(value) < 0) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-positive"
						);
					}
					if (isNaN(Number(value))) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-number"
						);
					}
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-24 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400",
							parameter.error && "!border-destructive"
						)}
					>
						{parameter.error ? (
							<TriangleAlertIcon className="stroke-destructive" />
						) : (
							"G"
						)}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.base-compensation"
						)}
						content={content}
						error={parameter.error}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MaxCompensationInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"max-compensation">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[115px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						G Max
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.max-compensation"
						)}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.max-compensation")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const MinCompensationInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"min-compensation">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-[115px] pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						G Min
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.min-compensation"
						)}
						error={parameter.error}
						content={t("admin.insurances.parameters.content.min-compensation")}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const BaseCompensationChildSupplementInput = ({
	store,
	insurance,
	parameter,
	alwaysExpanded = false,
}: ParameterInputProps<"base-compensation-child-supplement">) => {
	const hasChildSupplement = insurance.parameters?.find(
		(p) => p.type === "child-supplement"
	)?.value;

	if (!hasChildSupplement && !alwaysExpanded) {
		return null;
	}

	return (
		<div className="relative inline-flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					const value = e.target.value;
					parameter.value = value;
					parameter.error = null;

					if (value.length > 5) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-max"
						);
					}

					if (Number(value) < 0) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-positive"
						);
					}

					if (isNaN(Number(value))) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-number"
						);
					}

					store.editParameter(insurance, parameter);
				}}
				className={cn("w-24 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400",
							parameter.error && "!border-destructive"
						)}
					>
						{parameter.error ? (
							<TriangleAlertIcon className="stroke-destructive" />
						) : (
							"G BT"
						)}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.base-compensation-child-supplement"
						)}
						content={t(
							"admin.insurances.parameters.content.base-compensation-child-supplement"
						)}
						error={parameter.error}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const BaseCompensationDependentsSupplementInput = ({
	store,
	insurance,
	parameter,
	alwaysExpanded = false,
}: ParameterInputProps<"base-compensation-dependents-supplement">) => {
	const hasDependentsSupplement = insurance.parameters?.find(
		(p) => p.type === "dependents-supplement"
	)?.value;

	if (!hasDependentsSupplement && !alwaysExpanded) {
		return null;
	}

	return (
		<div className="relative inline-flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					const value = e.target.value;
					parameter.value = value;
					parameter.error = null;

					if (value.length > 5) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-max"
						);
					}

					if (Number(value) < 0) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-positive"
						);
					}

					if (isNaN(Number(value))) {
						parameter.error = t(
							"admin.insurances.parameters.error.base-compensation-number"
						);
					}

					store.editParameter(insurance, parameter);
				}}
				className={cn("w-24 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400",
							parameter.error && "!border-destructive"
						)}
					>
						{parameter.error ? (
							<TriangleAlertIcon className="stroke-destructive" />
						) : (
							"G FT"
						)}
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.base-compensation-dependents-supplement"
						)}
						content={t(
							"admin.insurances.parameters.content.base-compensation-dependents-supplement"
						)}
						error={parameter.error}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};

export const CoordinationAmountInput = ({
	store,
	insurance,
	parameter,
}: ParameterInputProps<"coordination-amount">) => {
	return (
		<div className="relative flex">
			<Input
				type="number"
				onWheel={(e) => (e.target as HTMLInputElement).blur()}
				value={parameter.value}
				onChange={(e) => {
					parameter.value = e.target.value;
					store.editParameter(insurance, parameter);
				}}
				className={cn("w-24 pr-12", getInputClass(parameter))}
			/>
			<Popover>
				<PopoverTrigger asChild>
					<InputSideLabel
						className={cn(
							"cursor-pointer",
							parameter.isAltered && "border-orange-400"
						)}
					>
						G Sam
					</InputSideLabel>
				</PopoverTrigger>
				<PopoverContent>
					<InputSideLabelPopover
						title={t(
							"admin.insurances.parameters.type-to-name.coordination-amount"
						)}
						content={t("admin.insurances.parameters.content.coordination-amount")}
						error={parameter.error}
						isAltered={parameter.isAltered}
						resetParam={() => store.resetParameter(insurance, parameter)}
					/>
				</PopoverContent>
			</Popover>
		</div>
	);
};
